import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { GeneralSelect } from 'sharedComponents/GeneralSelect';
import { mapFromFormattedSelectedOption, mapToFormattedOptions, mapToFormattedSelectedOptions } from './helpers';
import Option from './Option';

import './style.scss';
import { Virtuoso } from 'react-virtuoso';

const MenuList = ({ children, selectProps }) => {
    const { t: translate } = useTranslation();
    const { onSearchFromNewOrganizations, inputValue, isCiamSearchOrganizations, isLoading } = selectProps;
    // filterOption is disabled on the Select, so filter the matching options ourselves to keep the menu open on zero matches
    const matchingChildren = Array.isArray(children)
        ? children.filter((child) => child.props.data.label.toLowerCase().includes((inputValue || '').toLowerCase()))
        : [];
    const hasOptions = matchingChildren.length > 0;
    const linesCountofFirstElemnt = hasOptions ? Math.ceil(matchingChildren[0].props.children.length / 30) : 1;

    const onClickSearchFromNewOrganizations = () => {
        onSearchFromNewOrganizations(inputValue);
    };

    return <>
        {hasOptions ?
            <Virtuoso
                style={{ height: `${Math.max(Math.min(32 * matchingChildren.length, 224), linesCountofFirstElemnt * 32)}px` }}
                totalCount={matchingChildren.length}
                itemContent={(index) => matchingChildren[index]}
            />
            :
            <div className='no-organizations-found'>
                {translate('ABB.Powertrain.Frontend.generalFiltersNoOrganizationsFound')}
            </div>
        }
        {!isLoading &&
            <button
                type='button'
                className='search-new-organizations-link'
                // Prevent the select menu from closing before the click handler fires
                onClick={onClickSearchFromNewOrganizations}
            >
                {isCiamSearchOrganizations ?
                    translate('ABB.Powertrain.Frontend.generalFiltersBackToExistingOrganizations')
                    : translate('ABB.Powertrain.Frontend.generalFiltersSearchNewOrganizations')}
            </button>
        }
    </>;
};
// Add PropTypes validation for MenuList
MenuList.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node), // Array of React nodes
        PropTypes.node // A single React node
    ]).isRequired, // Mark as required
    selectProps: PropTypes.shape({
        onSearchFromNewOrganizations: PropTypes.func.isRequired,
        inputValue: PropTypes.string,
        isLoading: PropTypes.bool.isRequired,
        isCiamSearchOrganizations: PropTypes.bool.isRequired
    }).isRequired
};

const OrganizationSelector = (props) => {

    const [options, setOptions] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState([]);

    useEffect(() => {
        if (props?.options?.length) {
            const opt = mapToFormattedOptions(props.options);
            setOptions(opt);
        }
    }, [props.options]);

    useEffect(() => {
        setSelectedOptions(mapToFormattedSelectedOptions(props.selectedOrganization));
    }, [props.selectedOrganization]);

    const onChange = (selection) => {
        props.onChange(mapFromFormattedSelectedOption(selection));
    };

    const {
        ...otherProps
    } = props;

    return <GeneralSelect
        {...otherProps}
        getOptionLabel={option => option.label}
        getOptionValue={option => option.value}
        onChange={onChange}
        options={options}
        value={selectedOptions}
        className='organization-selector-container'
        blurInputOnSelect={true}
        isOptionDisabled={() => props.isLoading}
        closeMenuOnSelect={true}
        components={{
            Option,
            MenuList
        }}
        hideSelectedOptions={false}
        // Never let react-select filter options down to zero, so the menu stays open and MenuList can render the 'no results' UI
        filterOption={() => true}
    />;
};

OrganizationSelector.propTypes = {
    isLoading: PropTypes.bool, // Indicates if the selector is in a loading state
    onChange: PropTypes.func.isRequired, // Callback function for when the selection changes
    onSearchFromNewOrganizations: PropTypes.func.isRequired, // Callback function for when the 'Search from new organizations' link
    options: PropTypes.arrayOf(
        PropTypes.shape({
            organizationID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            name: PropTypes.string,
            guid: PropTypes.string,
            externalId: PropTypes.string,
            dataSourceType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            isPowertrainOrganization: PropTypes.bool
        })
    ).isRequired, // Array of raw organization options
    selectedOrganization: PropTypes.shape({
        organizationID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        guid: PropTypes.string,
        externalId: PropTypes.string,
        dataSourceType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        isPowertrainOrganization: PropTypes.bool
    }) // Raw selected organization
};


export default OrganizationSelector;
